import { memo, Suspense, useState } from "react";

import useAirplane from "../hooks/useAirplane.js";

import { type AirplaneInit, hooks } from "@/store.js";
import isEqual from "react-fast-compare";
import SeatIcon from "@/components/SeatIcon/index.jsx";
import useEdit from "./Airplane/useEdit.jsx";
import useMenu from "./Airplane/useMenu.jsx";
import useAirplaneTime from "@/hooks/useAirplaneTime.js";
import { AIRPLANE_COMPANY_ICON } from "@/const.js";
import AirplaneCard from "@/components/AirplaneCard/index.jsx";
import { UseAirplaneDetail } from "./Airplane/Detail/index.jsx";
import { useMemoizedFn } from "ahooks";

interface IProps {
	no: string;
	date: string;
	planId: string;
	id: string;

	previewValue?: Partial<AirplaneInit>;
}

const ExpandIcon = memo(
	({
		onChange,
		isExpand,
	}: {
		onChange: (isExpand: boolean) => void;
		isExpand: boolean;
	}) => (
		<i
			className={`ic-round md-${
				isExpand ? "expand_less" : "expand_more"
			} text-lg leading-none bg-black/5 opacity-70 rounded-full p-0.5 text-gray-400 ml-1`}
			onClick={(e) => {
				e.stopPropagation();
				onChange(!isExpand);
			}}
		/>
	),
	isEqual,
);

const AirPlane = memo(
	({ no, date, planId, id: eventId, previewValue }: IProps) => {
		const data = hooks.useOneAirplane({
			index: {
				where: "no",
				equals: no,
			},
		});
		const {
			checkedBagKg,
			seats: seatsEntity,
			counter,
			gate,
		} = { ...data?.getAll(), ...previewValue };
		const airplaneInfo = useAirplane(no) || {};
		const {
			company,
			model,

			startTime,
			endTime,
		} = airplaneInfo;

		const { placeholder: EditPlaceholder, show: showEdit } = useEdit();

		const seats = seatsEntity
			?.map((i) => i.map((n) => n))
			.filter(([name, position]) => position);
		const id = data?.get("id");

		const { placeholder: menuPlaceholder, show: showMenu } = useMenu({
			showEdit,
			id,
			planId,
			eventId,
		});

		const [isExpand, setIsExpand] = useState(
			() => new Date(`${date} 23:59:59`) > new Date(),
		);
		const [canExpand] = useState(() => !isExpand);
		const { startTimeText, endTimeText } = useAirplaneTime({
			date,
			startTime,
			endTime,
		});

		const detailModel = UseAirplaneDetail();
		const showDetail = useMemoizedFn(() => {
			detailModel.show({
				no,
				planId,
				date,
				id,
				defaultValue: { ...data?.getAll(), seats },
			});
		});

		if (!isExpand)
			return (
				<>
					<article className="bg-[#B8FBFF] rounded-2xl   " onClick={showDetail}>
						<header className="px-3 py-3 text-xs flex items-center text-gray-700  ">
							<span className="font-[600] flex items-center ">
								{company && AIRPLANE_COMPANY_ICON[company] ? (
									<i
										className="mr-1 w-5 h-5 bg-center bg-cover block"
										style={{
											backgroundImage: `url(${AIRPLANE_COMPANY_ICON[company]})`,
										}}
									/>
								) : (
									<i className="mr-1.5 ic-round md-airplanemode_active w-5 h-5 rotate-45 rounded-full text-white bg-blue-400 text-center leading-5 text-[13px]" />
								)}
							</span>
							<span className="flex-grow">{no}</span>

							<div className="flex items-center justify-between px-3  relative">
								<div>
									<b className="block font-[din] text-sm leading-none  text-gray-900 ">
										{startTimeText}
									</b>
								</div>
								-
								<div className="text-right">
									<b className="font-[din] text-sm leading-none relative block text-gray-900">
										{endTimeText}
									</b>
								</div>
							</div>

							<ExpandIcon isExpand={isExpand} onChange={setIsExpand} />
						</header>
					</article>

					{menuPlaceholder}

					{EditPlaceholder}
				</>
			);

		return (
			<>
				<AirplaneCard
					date={date}
					headerRight={
						canExpand && (
							<ExpandIcon isExpand={isExpand} onChange={setIsExpand} />
						)
					}
					no={no}
					{...airplaneInfo}
					onClick={showDetail}
					footer={
						<div className="rounded-xl mt-3 bg-[#DDFFFF] text-xs grid grid-cols-2 px-3 py-3 ">
							{[
								{ label: "行李", value: checkedBagKg || "-" },
								{
									label: "座位",
									value: seats?.length
										? seats.map(([name, line, no], index, array) => {
												const position = `${line}${no}`;
												return (
													<span key={name} className="inline-flex items-center">
														<SeatIcon type={position.slice(-1)} model={model} />
														{position}({name})
														{index !== array.length - 1 && (
															<i className="mr-2.5" />
														)}
													</span>
												);
											})
										: "-",
								},
								{ label: "值机台", value: counter || "-" },
								{ label: "登机口", value: gate || "-" },
							].map((item) => (
								<span
									key={item.label}
									className={`text-gray-700/80 py-1 flex items-center ${
										item.label === "座位" ? "-mr-2" : ""
									}`}
								>
									<span className="w-12 inline-block">{item.label}</span>
									<b className={"text-gray-700 flex items-center  font-400 "}>
										{item.value}
									</b>
								</span>
							))}
						</div>
					}
				/>

				{menuPlaceholder}

				{EditPlaceholder}
			</>
		);
	},
	isEqual,
);

const Skeleton = memo(() => {
	return <article className="bg-[#B8FBFF] rounded-2xl h-[175px]" />;
}, isEqual);
export default memo((props: IProps) => {
	return (
		<Suspense fallback={<Skeleton />}>
			<AirPlane {...props} />
		</Suspense>
	);
}, isEqual);
